<script setup lang="ts">
import type * as pf from '@amirada/lib-shared'
import type * as pfWilo from '@amirada/lib-wilo-shared'
import { ExtCmdType } from '@amirada/lib-wilo-shared'
import type * as tp from '@base/utils/types'
import { ut_buildParamData } from '@base/utils/utils'
import { useAppIndexStore } from '@base/stores/indexStore'
import { useDetailStore } from '@base/stores/detailStore'
import { usePfinderAPI } from '~/composables/pfinderAPI'

/*
 * Digital product pass: a serial number in, the product it names on screen.
 *
 * One call does the whole chain — the server reads the SAP booking row, resolves its article number
 * to the product, and answers with the ordinary detail plus the typekey sheet at the front of the
 * first tab. So this component never searches, never assembles and never renders a passport of its
 * own: it hands the answer to <Detail>, which renders it the way it renders every product.
 *
 * ⚑ The URL does not change. The customer wants the page to stay where it is and only exchange its
 * content, which is why the answer is put into the detail store under an id of our own instead of
 * navigating to the product.
 */
const p = defineProps<{
  isHistoric: boolean
  isMotors: boolean
  isInvisibleOem: boolean
  isServices: boolean
  isProducts: boolean
  isInternal: boolean
  countryLanguage: string
  realCountry: string
}>()

const indexStore = useAppIndexStore()
const detailStore = useDetailStore()
const pfinderAPI = usePfinderAPI()

const serial = ref('')
const searched = ref(false)

/*
 * The store key this answer lives under, and the `objPath` <Detail> reads it back with.
 *
 * A random id, not the product's real namePath. Under the real path our answer — a detail with the
 * typekey sheet mixed in — would sit under exactly the key the ordinary navigation reads later, and
 * that navigation would get the doctored version. A random id cannot collide with anything.
 *
 * That it means nothing is harmless: the entry is read once. Country and language live in the URL,
 * so changing either navigates away and this view is gone; a reload empties the store. There is no
 * moment in which a stale entry is read back.
 */
const objPath: Ref<string> = ref('')

async function lookup(): Promise<void> {
  const value = serial.value.trim()
  if (!value) { return }
  objPath.value = ''
  try {
    // No spinner of its own: fetchData switches the app-wide loading indicator on unless it is told
    // noLoading, and that is the one every other search on this site shows. A second one inside the
    // field would only be a different answer to the same question.
    const args: pfWilo.ExtCmd<ExtCmdType.DPP_DETAIL>['arg'] = { serial: value }
    const res = await pfinderAPI.fetchData(ExtCmdType.DPP_DETAIL, args, {})
    const detail = res?.result as pf.TPfNode | undefined
    if (detail) {
      // Seed and read through the same two functions, so the key cannot drift between writing it
      // here and <Detail> looking it up: callDetailData short-circuits on a hit, so no second
      // request goes out for data we already hold.
      const id: string = 'dpp/' + (globalThis.crypto?.randomUUID?.() || Date.now().toString(36))
      const param: tp.IParamData = ut_buildParamData(indexStore.state.paramData, { objPath: id })
      detailStore.setDetailData(param, detail)
      objPath.value = id
    }
  }
  catch {
    objPath.value = ''
  }
  finally {
    searched.value = true
  }
}
</script>

<template>
  <UContainer>
    <h1 class="wrap-break-word mb-4" v-html="indexStore.state.currentNode.desc" />

    <UInput
      v-model="serial"
      class="w-full max-w-200"
      size="xl"
      placeholder="Serial number…"
      autocomplete="off"
      spellcheck="false"
      @keydown.enter.stop.prevent="lookup"
    >
      <template #trailing>
        <UButton
          icon="i-wilo-magnifying-glass-solid-full"
          variant="ghost"
          color="tertiary"
          :disabled="serial.length === 0"
          @click="lookup"
        />
      </template>
    </UInput>

    <!-- Nothing to show: the serial is not in prodord_serial, so there is no SAP row behind it. -->
    <div v-if="searched && !objPath" class="mt-8" v-html="'No result found for this serial number.'" />
  </UContainer>

  <!--
    Outside the UContainer above, and with the spacing on a wrapper rather than on the component.

    Detail.vue brings its own UContainer, so nesting it inside ours would indent the product against
    the heading. And its template root is a `template v-if` — a fragment — which Vue cannot pass a
    class down to, so a class on the component itself is silently dropped.

    is-config and is-series are false by construction: an article number always names a product. The
    rest are the flags AppIndex works out for the page and passes through — they describe the
    catalogue this page sits in, and the product is shown inside it.
  -->
  <div v-if="objPath" class="mt-8">
    <LazyDetail
      :key="objPath"
      :obj-path="objPath"
      :is-dpp="true"
      :is-config="false"
      :is-series="false"
      :is-historic="p.isHistoric"
      :is-motors="p.isMotors"
      :is-invisible-oem="p.isInvisibleOem"
      :is-services="p.isServices"
      :is-products="p.isProducts"
      :is-internal="p.isInternal"
      :country-language="p.countryLanguage"
      :real-country="p.realCountry"
    />
  </div>
</template>
